import memoize from 'lru-memoize';
import React, {ReactNode} from 'react';

import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {makeEnumTypeGuard} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {
  AnalyticsGraphMetricsEnum,
  AnalyticsHeatMapMetricsEnum,
  AnalyticsMetricsKeys,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../models/api/analyticsMetricsModel';
import {
  AnalyticsOptionsBucketBoundaries,
  AnalyticsReportFilterOptions,
} from '../../models/api/analyticsSavedViewModel';
import {AnalyticsComputedGraphMetricsEnum} from '../transformers/analyticsMetricsTransformersTypes';

/*
 * Types.
 */

export type ReportMetricConfig = {
  id: AnalyticsMetricsKeys;
  isDisplayedByDefault: boolean;
};

export type ReportConfigElement = {
  id: ReportElementIdsEnum;
  // All metrics available on that report element. The order will be used in metric picker dropdowns.
  metrics: ReadonlyArray<ReportMetricConfig>;
};

type ReportConfig = {
  title: ReactNode;
  reportElements: ReadonlyArray<ReportConfigElement>;
  options?: AnalyticsReportFilterOptions;
};

export enum ReportFilterOptionsEnum {
  /*
   * The `customers_table` metric, by default, returns a mix of card IDs and contact IDs. This option forces it to
   * always return contact IDs.
   */
  ALWAYS_EMIT_CONTACT_ID = 'always_emit_contact_id',
  BUCKET_BOUNDARIES = 'bucket_boundaries', // Set custom buckets for donut chart.
  COLLECT_CONTRIBUTING_SEGMENT_IDS = 'collect_contributing_segment_ids',
  SELECTED_METRICS = 'selected_metrics',
  TAG_MODE = 'tag_mode', // Is the tag still applied or was it applied during period?
  /*
   * By explicitly specifying which metrics we need to have calculated for the previous period we can avoid doing so
   * unnecessarily.
   */
  PREVIOUS_PERIOD_METRICS = 'previous_period_metrics',
}

export enum ContributingSegmentsOptionsEnum {
  ROW = 'row',
  SUB_METRIC = 'sub_metric',
}

export interface BasicReportProps {
  isLoading: boolean;
}

export enum AnalyticsReportsEnum {
  OVERVIEW = 'overview',
  TEAM_PERF = 'team_performance',
  CONVERSATIONS = 'conversations',
  TAGS = 'tags',
  MESSAGE_TEMPLATES = 'message_templates',
  SLA = 'sla',
  CUSTOMER_EXPERIENCE = 'customer_experience',
}

export enum ReportElementIdsEnum {
  // OVERVIEW
  OVERVIEW_METRICS_CARDS = 'overview-metrics-cards',
  OVERVIEW_RESOLVED_PCT = 'resolved-segments-pct',
  OVERVIEW_CHANGES_OVER_TIME = 'overview-changes-over-time',
  OVERVIEW_REPLY_TIMES_WRAPPER = 'reply-times-teammates-and-tags',
  OVERVIEW_REPLY_TIMES_DONUT = 'reply-times-donut',
  OVERVIEW_REPLY_TIMES_HISTOGRAM = 'reply-time-histogram',
  OVERVIEW_TEAM_CHANGES_HISTOGRAM = 'team-changes-list',
  OVERVIEW_TAGS_HISTOGRAM = 'top-tags-list',
  // TEAM PERF
  TEAM_METRICS_CARDS = 'team-metrics-cards',
  TEAM_HEATMAP = 'busiest-times',
  TEAM_TEAMMATES_TABLE = 'teammates-table',
  // CONVERSATIONS
  CONVERSATIONS_WHAT_HAPPENED = 'what-happened',
  CONVERSATIONS_SANKEY = 'conversation-flows',
  CONVERSATIONS_INBOXES = 'inboxes',
  // TAGS
  TAGS_CONVERSATIONS_PCT = 'conversation-with-tags',
  TAGS_PERFORMANCE_TABLE = 'individual-tag-performance',
  TAG_CHANGES_TABLE = 'tag-changes',
  // MESSAGE TEMPLATES
  MESSAGE_TEMPLATES_PCT = 'messages-with-templates',
  MESSAGE_TEMPLATE_TABLE = 'message-template-table',
  // SLA
  SLA_METRICS_CARDS = 'sla-metrics-cards',
  SLA_PERF_GRAPH = 'sla-performance-graph',
  SLA_PERF_GRAPH_V2 = 'sla-performance-graph-v2',
  SLA_STATUS_TABLE_V2 = 'sla-status-table-v2',
  // CUSTOMER EXPERIENCE
  CX_TOTAL_SCALAR = 'customer-experience-total-scalar',
  CX_SCORES_DONUT_WRAPPER = 'customer-experience-scores-donut',
  CX_SCORES_LIST_WRAPPER = 'customer-experience-scores-list',
  CX_SCORES_DONUT = 'customer-experience-scores-donut',
  CX_SCORES_AVG = 'customer-experience-scores-average',
  CX_CHANGES_OVER_TIME_CHART = 'customer-experience-changes-over-time',
  CX_SAMPLE_FEEDBACK_TABLE = 'customer-experience-report-sample-feedback-table',
  CX_TABLE = 'customer-experience-report-table',
  CX_CUSTOMERS_TABLE_V2 = 'customer-experience-report-customers-table-v2',
  CX_ACCOUNTS_TABLE = 'customer-experience-report-accounts-table',
}

/*
 * Helpers.
 */

// These are behind the "analytics-resolution-metrics" FF for now.
const overviewScalarResolutionMetrics = [
  {id: AnalyticsScalarMetricsEnum.NUM_RESOLVED_SEGMENTS, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.NUM_UNRESOLVED_ACTIVE_SEGMENTS, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.AVG_RESOLUTION_TIME, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.PCT_RESOLVED_ON_FIRST_REPLY, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.AVG_REPLIES_TO_RESOLUTION, isDisplayedByDefault: false},
];

export const overviewGraphResolutionMetrics = [
  {id: AnalyticsGraphMetricsEnum.NUM_RESOLVED_SEGMENTS_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.AVG_RESOLUTION_TIME_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.PCT_RESOLVED_ON_FIRST_REPLY_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.AVG_REPLIES_TO_RESOLUTION_GRAPH, isDisplayedByDefault: false},
];

const overviewHistogramResolutionMetrics = [
  {id: AnalyticsTableMetricsEnum.AVG_REPLIES_TO_RESOLUTION_HISTO, isDisplayedByDefault: false},
];

const overviewPctResolvedReportElement = {
  // Progress bar.
  id: ReportElementIdsEnum.OVERVIEW_RESOLVED_PCT,
  metrics: [
    {id: AnalyticsScalarMetricsEnum.PCT_RESOLVED_SEGMENTS, isDisplayedByDefault: true}, // Will be transformed to 'RESOLVED_SEGMENTS_PROGRESS'.
  ],
};

export const analyticsResolutionMetrics = [
  ...overviewScalarResolutionMetrics.map((metric) => metric.id),
  ...overviewGraphResolutionMetrics.map((metric) => metric.id),
  ...overviewHistogramResolutionMetrics.map((metric) => metric.id),
  ...overviewPctResolvedReportElement.metrics.map((metric) => metric.id),
];

export const getAnalyticsReportConfig = memoize()(
  (reportType: AnalyticsReportsEnum, hasResolutionMetrics: boolean): ReportConfig =>
    ({
      [AnalyticsReportsEnum.OVERVIEW]: {
        title: <Trans id="analytics-title-overview">Overview</Trans>,
        reportElements: [
          {
            // Hero cards.
            id: ReportElementIdsEnum.OVERVIEW_METRICS_CARDS,
            metrics: [
              {id: AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.MESSAGES_SENT, isDisplayedByDefault: false},
              {id: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED, isDisplayedByDefault: false},
              {id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME, isDisplayedByDefault: false},
              {id: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME, isDisplayedByDefault: false},
              // Dynamically inserting resolution metrics if the FF is on.
              ...((hasResolutionMetrics && overviewScalarResolutionMetrics) || []),
              {id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY, isDisplayedByDefault: false},
            ],
          },
          // Dynamically inserting the % of resolved conversations metric if the FF is on.
          ...((hasResolutionMetrics && [overviewPctResolvedReportElement]) || []),
          {
            // Changes over time chart.
            id: ReportElementIdsEnum.OVERVIEW_CHANGES_OVER_TIME,
            metrics: [
              {id: AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH, isDisplayedByDefault: true},
              {id: AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH, isDisplayedByDefault: false},
              {id: AnalyticsGraphMetricsEnum.RESPONSE_GRAPH, isDisplayedByDefault: false},
              {id: AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH, isDisplayedByDefault: false},
              {id: AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH, isDisplayedByDefault: false},
              {id: AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH, isDisplayedByDefault: false},
              // Dynamically inserting resolution metrics if the FF is on.
              ...((hasResolutionMetrics && overviewGraphResolutionMetrics) || []),
              {id: AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH, isDisplayedByDefault: false},
              {id: AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH, isDisplayedByDefault: false},
            ],
          },
          {
            // Reply times list and Donut.
            id: ReportElementIdsEnum.OVERVIEW_REPLY_TIMES_WRAPPER,
            metrics: [
              {id: AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM, isDisplayedByDefault: true},
              {id: AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM, isDisplayedByDefault: false},
              // Dynamically inserting resolution metrics if the FF is on.
              ...((hasResolutionMetrics && overviewHistogramResolutionMetrics) || []),
            ],
          },
          {
            // Team changes list.
            id: ReportElementIdsEnum.OVERVIEW_TEAM_CHANGES_HISTOGRAM,
            metrics: [{id: AnalyticsTableMetricsEnum.TEAM_CHANGES, isDisplayedByDefault: true}],
          },
          {
            // Top tags list.
            id: ReportElementIdsEnum.OVERVIEW_TAGS_HISTOGRAM,
            metrics: [{id: AnalyticsTableMetricsEnum.TOP_TAGS, isDisplayedByDefault: true}],
          },
        ],
        options: {
          [ReportFilterOptionsEnum.BUCKET_BOUNDARIES]: {
            // Default buckets for the reply time histogram metrics: < 15min, 15min - 45min, 45m - 1h, 1h - 2h, > 2h
            [AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM]: [900000, 2700000, 3600000, 7200000],
            [AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM]: [900000, 2700000, 3600000, 7200000],
            // Default buckets for the avg replies to res metric: 1 reply, 2 replies, 3 to 4 replies, 5 to 7 replies, 8+ replies.
            [AnalyticsTableMetricsEnum.AVG_REPLIES_TO_RESOLUTION_HISTO]: [1, 1, 2, 2, 3, 4, 5, 7, 8],
          },
          [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [
            AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
            AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS,
            AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
            AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS,

            AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY,
            AnalyticsScalarMetricsEnum.MESSAGES_SENT,
            AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
            AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
            AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,

            AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH,
            AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH,
            AnalyticsGraphMetricsEnum.RESPONSE_GRAPH,
            AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH,
            AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH,
            AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
            // Dynamically inserting resolution metrics if the FF is on.
            ...((hasResolutionMetrics && overviewGraphResolutionMetrics.map((metric) => metric.id)) || []),
            AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH,
            AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH,

            AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
            AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM,
            AnalyticsTableMetricsEnum.AVG_REPLIES_TO_RESOLUTION_HISTO,

            AnalyticsTableMetricsEnum.TEAM_CHANGES,

            AnalyticsTableMetricsEnum.TOP_TAGS,
          ],
        },
      },

      [AnalyticsReportsEnum.TEAM_PERF]: {
        title: <Trans id="analytics-title-team_performance">Team Performance</Trans>,
        reportElements: [
          {
            // Hero cards.
            id: ReportElementIdsEnum.TEAM_METRICS_CARDS,
            metrics: [
              {id: AnalyticsScalarMetricsEnum.MESSAGES_SENT, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.AVG_TOTAL_REPLY_TIME, isDisplayedByDefault: false},
              {id: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY, isDisplayedByDefault: false},
              {id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS, isDisplayedByDefault: false},
              {id: AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES, isDisplayedByDefault: false},
            ],
          },
          {
            // Busiest times heatmap.
            id: ReportElementIdsEnum.TEAM_HEATMAP,
            metrics: [{id: AnalyticsHeatMapMetricsEnum.INBOX, isDisplayedByDefault: true}],
          },
          {
            // Teammates perf table.
            id: ReportElementIdsEnum.TEAM_TEAMMATES_TABLE,
            metrics: [{id: AnalyticsTableMetricsEnum.TEAM_PERFORMANCE, isDisplayedByDefault: true}],
          },
        ],
        options: {
          [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [
            AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
            AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
            AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES,
            AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
            AnalyticsScalarMetricsEnum.MESSAGES_SENT,
            AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY,
            AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS,
          ],
        },
      },

      [AnalyticsReportsEnum.CONVERSATIONS]: {
        title: <Trans id="analytics-title-conversations">Conversations</Trans>,
        reportElements: [
          {
            // What happened chart.
            id: ReportElementIdsEnum.CONVERSATIONS_WHAT_HAPPENED,
            metrics: [
              {id: AnalyticsTableMetricsEnum.WHAT_HAPPENED_TABLE, isDisplayedByDefault: true}, // Will be transformed to `WHAT_HAPPENED_GRAPH`.
            ],
          },
          {
            // Conversation flows sankey.
            id: ReportElementIdsEnum.CONVERSATIONS_SANKEY,
            metrics: [
              {id: AnalyticsTableMetricsEnum.SANKEY_TABLE, isDisplayedByDefault: true}, // Will be transformed to `SANKEY_GRAPH`.
            ],
          },
          {
            // Inboxes table.
            id: ReportElementIdsEnum.CONVERSATIONS_INBOXES,
            metrics: [{id: AnalyticsTableMetricsEnum.INBOX_TABLE, isDisplayedByDefault: true}],
          },
        ],
        options: {
          // TODO Remove this when the BE changes the semantic for omitting this option to "no previous period metrics".
          [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [],
        },
      },

      [AnalyticsReportsEnum.MESSAGE_TEMPLATES]: {
        title: <Trans id="analytics-title-message-templates">Message Templates</Trans>,
        reportElements: [
          {
            // Progress bar.
            id: ReportElementIdsEnum.MESSAGE_TEMPLATES_PCT,
            metrics: [
              {
                id: AnalyticsScalarMetricsEnum.PCT_MESSAGES_WITH_MESSAGE_TEMPLATES,
                isDisplayedByDefault: true,
              }, // Will be transformed to 'MESSAGE_TEMPLATE_REPORT_PROGRESS'.
            ],
          },
          {
            // Message Templates Table.
            id: ReportElementIdsEnum.MESSAGE_TEMPLATE_TABLE,
            metrics: [{id: AnalyticsTableMetricsEnum.MESSAGE_TEMPLATE_TABLE, isDisplayedByDefault: true}],
          },
        ],
        options: {
          // TODO Remove this when the BE changes the semantic for omitting this option to "no previous period metrics".
          [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [],
        },
      },

      [AnalyticsReportsEnum.TAGS]: {
        title: <Trans id="analytics-title-tags">Tags</Trans>,
        reportElements: [
          {
            // Progress bar.
            id: ReportElementIdsEnum.TAGS_CONVERSATIONS_PCT,
            metrics: [
              {id: AnalyticsScalarMetricsEnum.PCT_TAGGED_CONVERSATIONS, isDisplayedByDefault: true}, // Will be transformed to 'TAG_REPORT_PROGRESS'.
            ],
          },
          {
            // Tag perf table.
            id: ReportElementIdsEnum.TAGS_PERFORMANCE_TABLE,
            metrics: [{id: AnalyticsTableMetricsEnum.TAGS_ALL_TABLE, isDisplayedByDefault: true}],
          },
          {
            // Tag changes.
            id: ReportElementIdsEnum.TAG_CHANGES_TABLE,
            metrics: [{id: AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE, isDisplayedByDefault: true}],
          },
        ],
        options: {
          // TODO Remove this when the BE changes the semantic for omitting this option to "no previous period metrics".
          [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [],
        },
      },

      [AnalyticsReportsEnum.SLA]: {
        title: <Trans id="analytics-title-sla">Service Level Agreements</Trans>,
        reportElements: [
          {
            // Hero cards.
            id: ReportElementIdsEnum.SLA_METRICS_CARDS,
            metrics: [
              {id: AnalyticsScalarMetricsEnum.PCT_SLA_ACHIEVED, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.NUM_SLA_TRIGGER, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.SLA_BREACHES, isDisplayedByDefault: true},
              {id: AnalyticsScalarMetricsEnum.SLA_WARNINGS, isDisplayedByDefault: false},
              {id: AnalyticsScalarMetricsEnum.AVG_SLA_BREACH_TIME, isDisplayedByDefault: true},
            ],
          },
          {
            // SLA Perf graph.
            id: ReportElementIdsEnum.SLA_PERF_GRAPH_V2,
            metrics: [
              {id: AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH, isDisplayedByDefault: true},
              {id: AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH, isDisplayedByDefault: false},
              {id: AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH, isDisplayedByDefault: false},
            ],
          },
          {
            // SLA Table V2.
            id: ReportElementIdsEnum.SLA_STATUS_TABLE_V2,
            metrics: [{id: AnalyticsTableMetricsEnum.SLA_STATUS_TABLE_V2, isDisplayedByDefault: true}],
          },
        ],
        options: {
          [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [
            AnalyticsScalarMetricsEnum.PCT_SLA_ACHIEVED,
            AnalyticsScalarMetricsEnum.SLA_BREACHES,
            AnalyticsScalarMetricsEnum.SLA_WARNINGS,
            AnalyticsScalarMetricsEnum.AVG_SLA_BREACH_TIME,

            AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH,
            AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH,
            AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH,
          ],
        },
      },

      [AnalyticsReportsEnum.CUSTOMER_EXPERIENCE]: {
        title: <Trans id="analytics-title-customer_experience">Customer Satisfaction</Trans>,
        reportElements: [
          {
            // CX Total Survey Count Scalar.
            id: ReportElementIdsEnum.CX_TOTAL_SCALAR,
            metrics: [{id: AnalyticsScalarMetricsEnum.NUM_CSAT_SURVEY_RESPONSE, isDisplayedByDefault: true}],
          },
          {
            // CX Graph.
            id: ReportElementIdsEnum.CX_CHANGES_OVER_TIME_CHART,
            metrics: [
              {id: AnalyticsGraphMetricsEnum.CSAT_SURVEY_SATISFACTION_GRAPH, isDisplayedByDefault: true},
              {id: AnalyticsGraphMetricsEnum.CSAT_SURVEY_RESPONSE_GRAPH, isDisplayedByDefault: false},
            ],
          },
          {
            // Accounts Table.
            id: ReportElementIdsEnum.CX_ACCOUNTS_TABLE,
            metrics: [{id: AnalyticsTableMetricsEnum.ACCOUNTS_TABLE, isDisplayedByDefault: true}],
          },
          {
            // CX Table V2.
            id: ReportElementIdsEnum.CX_CUSTOMERS_TABLE_V2,
            metrics: [{id: AnalyticsTableMetricsEnum.CUSTOMERS_TABLE_V2, isDisplayedByDefault: false}],
          },
          {
            // Score summary Donut chart
            id: ReportElementIdsEnum.CX_SCORES_DONUT,
            metrics: [{id: AnalyticsTableMetricsEnum.CSAT_SURVEY_RESPONSE_HISTO, isDisplayedByDefault: true}],
          },
          // Donut report list.
          {
            id: ReportElementIdsEnum.CX_SCORES_LIST_WRAPPER,
            metrics: [{id: AnalyticsTableMetricsEnum.CSAT_SURVEY_RESPONSE_HISTO, isDisplayedByDefault: true}],
          },
          {
            // Score summary Donut chart
            id: ReportElementIdsEnum.CX_SCORES_AVG,
            metrics: [
              {id: AnalyticsScalarMetricsEnum.PCT_CSAT_SURVEY_SATISFACTION, isDisplayedByDefault: true},
            ],
          },
          {
            // CX Sample Feedback Table.
            id: ReportElementIdsEnum.CX_SAMPLE_FEEDBACK_TABLE,
            metrics: [
              {id: AnalyticsTableMetricsEnum.CSAT_SURVEY_SAMPLE_FEEDBACK_TABLE, isDisplayedByDefault: true},
            ],
          },
        ],
        options: {
          [ReportFilterOptionsEnum.ALWAYS_EMIT_CONTACT_ID]: true,
          // TODO Remove this when the BE changes the semantic for omitting this option to "no previous period metrics".
          [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [],
        },
      },
    }[reportType]),
);

export function getReportOptionsWithBucketBoundaries(
  reportType: AnalyticsReportsEnum,
  metrics: Array<string>,
  hasResolutionMetrics: boolean,
  bucketBoundaries?: AnalyticsOptionsBucketBoundaries,
): AnalyticsReportFilterOptions | undefined {
  const {options} = getAnalyticsReportConfig(reportType, hasResolutionMetrics);
  const rawOptions = {
    ...options, // eslint-disable-next-line camelcase
    bucket_boundaries: {...options?.bucket_boundaries, ...bucketBoundaries},
  };

  // Make sure we don't ask previous period options for metrics we don't compute.
  const sanitizedOptions = rawOptions.previous_period_metrics
    ? {
        ...rawOptions,
        previous_period_metrics: rawOptions.previous_period_metrics.filter((previousPeriodMetric) =>
          metrics.includes(previousPeriodMetric),
        ),
      }
    : rawOptions;

  return sanitizedOptions;
}

/**
 * For a given report element, returns the list of available metrics and metrics displayed by default.
 */
export function getReportElementMetrics<T extends AnalyticsMetricsKeys>(
  reportType: AnalyticsReportsEnum,
  reportElementId: ReportElementIdsEnum,
  hasResolutionMetrics: boolean,
) {
  const reportElementConfig = getAnalyticsReportConfig(reportType, hasResolutionMetrics).reportElements.find(
    (element) => element.id === reportElementId,
  );

  if (!reportElementConfig) {
    return {displayedMetrics: [], availableMetrics: []};
  }

  return getMetricsFromReportElementConfig<T>(reportElementConfig);
}

export function getMetricsFromReportElementConfig<T extends AnalyticsMetricsKeys>(
  reportElementConfig: ReportConfigElement,
) {
  const displayedMetrics = (reportElementConfig.metrics || [])
    .filter(({isDisplayedByDefault}) => isDisplayedByDefault)
    // TODO find a way to avoid the cast.
    .map((metricConfig) => metricConfig.id as T);

  const availableMetrics = (reportElementConfig.metrics || []).map((metricConfig) => metricConfig.id as T);

  return {
    displayedMetrics,
    availableMetrics,
  };
}

/*
 * Type guards.
 */

export const isGraphMetric = makeEnumTypeGuard(AnalyticsGraphMetricsEnum);
export const isScalarMetric = makeEnumTypeGuard(AnalyticsScalarMetricsEnum);
export const isComputedGraphMetric = makeEnumTypeGuard(AnalyticsComputedGraphMetricsEnum);
